
import { computed, defineComponent, onMounted, reactive } from 'vue';
import PointInfo from '@/components/organisms/point/PointInfo.vue';
import { usePoint } from '@/compositions/usePoint';
import axios from 'axios';
import { useRoute, useRouter } from 'vue-router/composables';
import { i18n } from '@/plugins/i18n';
import { useGeolocation } from '@/compositions/useGeolocation';

type State = {
  isLoading: boolean;
  hasError: boolean;
  success: boolean;
  result?: {
    point: number;
    type: 'GRANT'; // | 'RECEIVE,
    line: {
      link: string;
      displayName: string;
      pictureUrl?: string;
    };
  };
  statusCode?: number;
};

export default defineComponent({
  name: 'PagesPointAction',

  components: {
    PointInfo,
  },

  setup(_) {
    const { action } = usePoint();
    const route = useRoute();
    const router = useRouter();

    const state = reactive<State>({
      isLoading: false,
      hasError: false,
      success: false,
      result: undefined,
    });

    const { getLatLng } = useGeolocation();

    /**
     * CS-8234 再帰呼び出し用に関数化
     */
    const _callAction = async (latLng?: { lat: number; lng: number }, retryCount = 0) => {
      try {
        state.isLoading = true;
        const id = route.query.id as string | null;
        const token = route.query.token as string | null;
        const shopChId = route.query.shop_ch as string | null; // CS-6688
        if (id) {
          state.result = await action(id, token, shopChId, latLng?.lat ?? null, latLng?.lng ?? null);
          state.success = true; // 正常完了フラグを立てる
        }
        router.replace('/point_action/complete'); // ブラウザバックされたくないのでreplace
      } catch (e) {
        if (!axios.isAxiosError(e)) throw e;

        state.result = e.response && e.response.data;
        const { status } = e.response || {};
        state.statusCode = status; // 400 | 410 | 423 | 452 | 453 | 430/*CS-8234*/ | 484 /** CS-8875 */
        /**
         * QRコードが有効化されていない場合は隠蔽するために404扱いに
         * @see API仕様書 https://sonicmoov-saas.atlassian.net/wiki/spaces/CO/pages/136282385/API#%E3%82%AF%E3%83%BC%E3%83%9D%E3%83%B3%E4%BB%98%E4%B8%8E
         */
        if (status === 423) return await router.replace('/not_found');

        if (status === 430) {
          let latLng;
          try {
            // CS-8234 GPS緯度経度取得し再呼び出し
            // 1回目の呼び出しは基本エラーになる。
            latLng = await getLatLng();
          } catch (e) {
            console.log(e);

            if (retryCount >= 3) {
              console.log(`位置情報取得に${retryCount + 1}回失敗しました。`);
              await router.replace('/point_action/error');
              return;
            }
          }
          await _callAction(latLng, retryCount + 1);

          return;
        }

        // その他エラー
        await router.replace('/point_action/error');

        // 想定外のエラーの場合はthrowする
        if (!status || ![400, 410, 423, 452, 453, 431 /*CS-8234 位置が離れすぎ*/, 484].includes(status)) throw e;
      } finally {
        state.isLoading = false;
      }
    };

    onMounted(async () => {
      // Set title
      document.title = `${i18n.t('keyword.point')}取得`;

      //
      if (route.path === '/point_action/complete') return;
      if (route.path === '/point_action/error') return;

      await _callAction();
    });

    const line = computed(() => state.result?.line);

    return {
      state,
      line,
    };
  },
});
